import { useState } from "react";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { SECTORS } from "@/lib/sectors";
import { COMPANY } from "@/lib/company";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

export default function ContactPage() {
    const [form, setForm] = useState({ full_name: "", email: "", phone: "", postcode: "", sector_slug: "", message: "" });
    const [gdpr, setGdpr] = useState(false);
    const [loading, setLoading] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!gdpr) { toast.error("Please tick the GDPR consent box to continue."); return; }
        const sector = SECTORS.find((s) => s.slug === form.sector_slug);
        setLoading(true);
        try {
            await api.post("/enquiries", {
                sector_slug: form.sector_slug || "general-enquiry",
                sector_name: sector?.name || "General Enquiry",
                full_name: form.full_name,
                email: form.email,
                phone: form.phone || "0000000000",
                postcode: form.postcode || "UNKNOWN",
                details: { source: "contact-page", message: form.message },
                gdpr_consent: gdpr,
                marketing_consent: false,
            });
            toast.success("Thanks — we&apos;ll be in touch shortly.");
            setForm({ full_name: "", email: "", phone: "", postcode: "", sector_slug: "", message: "" });
            setGdpr(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Seo
                title="Contact ClaimHub UK"
                description={`Speak to a real claims specialist — ${COMPANY.phone} or ${COMPANY.email}. ${COMPANY.hours}. No call centres, no scripts.`}
                path="/contact"
            />
            <section className="bg-navy-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-mesh-emerald" />
                <div className="absolute inset-0 grid-bg-dark opacity-25" />
                <div className="container-x relative py-20 lg:py-24">
                    <span className="text-eyebrow-light">Get in touch</span>
                    <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight">We&apos;d love to hear from you.</h1>
                    <p className="mt-6 text-lg text-slate-300 max-w-2xl">No call centres, no scripts. Just real specialists ready to help. Reply within one working day, guaranteed.</p>
                </div>
            </section>

            <section className="section-pad bg-slate-50">
                <div className="container-x grid lg:grid-cols-12 gap-10">
                    <aside className="lg:col-span-4 space-y-4">
                        {[
                            { icon: Phone, label: "Call us", value: COMPANY.phone, sub: COMPANY.hours },
                            { icon: Mail, label: "Email us", value: COMPANY.email, sub: "Replies same working day" },
                            { icon: MapPin, label: "Visit us", value: COMPANY.addressShort, sub: "By appointment only" },
                        ].map((c) => (
                            <div key={c.label} className="card-soft p-6">
                                <div className="w-10 h-10 rounded-xl bg-navy-900 flex items-center justify-center">
                                    <c.icon className="w-5 h-5 text-emerald-400" />
                                </div>
                                <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-emerald-700">{c.label}</div>
                                <div className="mt-1 text-lg font-display font-semibold text-navy-900">{c.value}</div>
                                <div className="mt-1 text-xs text-slate-500">{c.sub}</div>
                            </div>
                        ))}
                    </aside>

                    <div className="lg:col-span-8">
                        <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 shadow-card-soft p-7 lg:p-10">
                            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-navy-900">Send us a message</h2>
                            <p className="mt-2 text-sm text-slate-500">Not sure if you have a claim? Send a quick note — we&apos;ll get back to you fast.</p>

                            <div className="mt-7 grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm font-semibold text-navy-900">Full name</Label>
                                    <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-2 h-12 rounded-xl" data-testid="contact-name-input" />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold text-navy-900">Email</Label>
                                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-12 rounded-xl" data-testid="contact-email-input" />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold text-navy-900">Phone <span className="text-slate-400 font-normal">(optional)</span></Label>
                                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 h-12 rounded-xl" data-testid="contact-phone-input" />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold text-navy-900">Topic</Label>
                                    <Select value={form.sector_slug} onValueChange={(v) => setForm({ ...form, sector_slug: v })}>
                                        <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="contact-topic-select">
                                            <SelectValue placeholder="Choose a topic (optional)" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SECTORS.map((s) => (
                                                <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-sm font-semibold text-navy-900">Your message</Label>
                                    <Textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 min-h-[140px] rounded-xl" data-testid="contact-message-input" />
                                </div>
                            </div>

                            <label className="mt-5 flex items-start gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer">
                                <Checkbox checked={gdpr} onCheckedChange={(v) => setGdpr(!!v)} data-testid="contact-gdpr" className="mt-0.5 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" />
                                <span className="text-sm text-slate-700">I agree to ClaimHub UK contacting me about my enquiry, in line with the Privacy Policy.</span>
                            </label>

                            <button type="submit" disabled={loading} data-testid="contact-submit-button" className="btn-primary mt-6 w-full sm:w-auto disabled:opacity-50">
                                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send message <Send className="w-4 h-4" /></>}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
}
